/**
 * FormSelect — Tier 0 slice-local native `<select>` primitive (SCRUM-337).
 *
 * The booking create-reservation form needs two single-choice selects — the
 * reservation-type picker and the "How did you hear about us?" (heardAbout)
 * enum — and `@sparta/ui-kit` ships NO select primitive today. Rather than
 * hand-roll a Radix listbox, we compose a real native `<select>`: native is
 * keyboard-accessible, mobile-native (iOS wheel picker), and a11y by default;
 * the visual styling only hides the OS chrome behind a brand-tinted chevron.
 *
 * This mirrors the property slice's `FilterSel` idiom (SCRUM-167) but is built
 * as a *form* field, not a filter pill: it renders a real visible `<label>`
 * wired to the select via `htmlFor`/`id` (so the field has a proper accessible
 * name and a label a sighted user reads), supports a `required` marker, an
 * inline error message (`role="alert"` + `aria-describedby`), and a leading
 * placeholder option. Generic over the option-id type so callers get exhaustive
 * narrowing on the `onChange` callback.
 *
 * **Tokens (ADR frontend-0003, SCRUM-78/79).** Colors + borders + radius come
 * from semantic `var(--token)`s only — no hex, no inline pixel literals.
 *
 * **Tier (ADR frontend-0004).** Tier 0: slice-local, NOT barrel-exported.
 * Promote on the second-consumer trigger (a second slice needing a form
 * select).
 */

import * as React from 'react';
import { Icon } from '@sparta/ui-kit';

export interface FormSelectOption<TId extends string> {
  /** Option value submitted / matched (never surfaced raw when it is an internal key). */
  readonly id: TId;
  /** Human-readable label rendered in the option list. */
  readonly label: string;
}

export interface FormSelectProps<TId extends string> {
  /** Stable id — wires the `<label htmlFor>` to the `<select>`. */
  readonly id: string;
  /** Visible label for the field (the select's accessible name). */
  readonly label: string;
  /** Currently-selected option id (`''` selects the placeholder). */
  readonly value: TId | '';
  /** Called with the next id on selection. */
  readonly onChange: (next: string) => void;
  /** Option list. */
  readonly options: ReadonlyArray<FormSelectOption<TId>>;
  /** Leading placeholder option label (value `''`). Omit for no placeholder. */
  readonly placeholder?: string;
  /** Marks the field required (visual marker + `aria-required`). */
  readonly required?: boolean;
  /** Inline error message — sets `aria-invalid` + `role="alert"`. */
  readonly error?: string;
  /** Optional non-error helper text below the field. */
  readonly hint?: string;
  /** Disable the control. */
  readonly disabled?: boolean;
}

export function FormSelect<TId extends string>({
  id,
  label,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  error,
  hint,
  disabled = false,
}: FormSelectProps<TId>): React.ReactElement {
  const hintId = hint !== undefined ? `${id}-hint` : undefined;
  const errorId = error !== undefined ? `${id}-error` : undefined;
  const describedBy =
    [hintId, errorId].filter((t): t is string => typeof t === 'string').join(' ') ||
    undefined;

  return (
    <div className="mb-4 block">
      <label
        htmlFor={id}
        className="mb-2 block text-[13px] font-semibold text-[var(--fg-secondary)]"
      >
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-1 text-[color:var(--fg-danger)]">
            *
          </span>
        ) : null}
      </label>
      <div className="relative block">
        <select
          id={id}
          value={value}
          required={required}
          aria-required={required}
          aria-invalid={error !== undefined}
          aria-describedby={describedBy}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="block w-full appearance-none min-h-touch rounded-xl border border-solid pr-10 pl-3 text-[15px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: 'var(--bg-surface)',
            color: 'var(--fg-primary)',
            borderColor:
              error !== undefined ? 'var(--fg-danger)' : 'var(--border-default)',
            font: 'inherit',
          }}
        >
          {placeholder !== undefined ? (
            <option value="">{placeholder}</option>
          ) : null}
          {options.map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
        <span
          aria-hidden="true"
          className="absolute inset-y-0 right-3 flex items-center pointer-events-none"
          style={{ color: 'var(--fg-tertiary)' }}
        >
          <Icon name="chevDown" size={16} />
        </span>
      </div>
      {hint !== undefined ? (
        <p id={hintId} className="mt-2 text-xs text-[var(--fg-tertiary)]">
          {hint}
        </p>
      ) : null}
      {error !== undefined ? (
        <p id={errorId} role="alert" className="mt-2 text-xs text-[var(--fg-danger)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}
